import { useState } from "react";
import { useTranslation } from "react-i18next";
import "./CoverageMap.css";

// Coordinates in a 1014×662 space, projected from each city's real lat/lon
// (equirectangular, bounds 33.45°/13.45°N, 119.7°/85.4°W — padded ~4%
// beyond Mexico's real extent on every side, so a dot/label right at the
// border, like Tijuana, has room before hitting this card's own edge) —
// every value here is computed straight from that formula, not eyeballed.
//
// public/coverage-map.svg is generated by the exact same formula (see
// scripts/generate-coverage-map.py — built from a real, labeled Mexico
// states GeoJSON, projected with these identical bounds), so a city's real
// lat/lon always lands in the right place on the map, by construction. An
// earlier version tried to calibrate these dots against a hand-drawn
// Illustrator base map instead; that map had no consistent projection (and
// no semantic layer data — every border/river/highway was a flat unlabeled
// path), so different dots needed different, inconsistent manual
// corrections to look right — some cities were subtly off everywhere, not
// just the one corner that was obviously broken. Replacing the base map
// itself, rather than special-casing dots against it, removes that whole
// class of mismatch.
const HUB = { id: "delicias", name: "Delicias", x: 421, y: 174 };

// Full city network, matching the reference route map exactly (name, node
// set and connections reviewed against it 1:1). Torreón and Saltillo are
// real labeled stops here (previously unlabeled "waypoints" — a mismatch
// with the reference, where both carry full labels like every other city).
const CITIES = [
  // Tijuana/Ensenada/Mexicali/Chihuahua + the bend points below were
  // hand-tuned by the user with tools/map-editor.html (a draggable local
  // editor built for exactly this) — not re-derived from the projection
  // formula, so don't "correct" these back toward it.
  { id: "tijuana", name: "Tijuana", x: 91.7, y: 34.8, label: { dx: -10, dy: -8, anchor: "end" } },
  { id: "mexicali", name: "Mexicali", x: 132.9, y: 34.7 },
  { id: "hermosillo", name: "Hermosillo", x: 258, y: 145 },
  { id: "chihuahua", name: "Chihuahua", x: 407, y: 154.7, label: { dx: 0, dy: -14, anchor: "middle" } },
  { id: "torreon", name: "Torreón", x: 482, y: 262, label: { dx: -11, dy: 3, anchor: "end" } },
  { id: "saltillo", name: "Saltillo", x: 553, y: 266, label: { dx: 0, dy: -14, anchor: "middle" } },
  { id: "monterrey", name: "Monterrey", x: 573, y: 257 },
  { id: "durango", name: "Durango", x: 445, y: 312 },
  { id: "zacatecas", name: "Zacatecas", x: 506, y: 353, label: { dx: -10, dy: -8, anchor: "end" } },
  { id: "aguascalientes", name: "Aguascalientes", x: 515, y: 383, label: { dx: 0, dy: 20, anchor: "middle" } },
  { id: "slp", name: "San Luis Potosí", x: 553, y: 374, label: { dx: -11, dy: -8, anchor: "end" } },
  { id: "guadalajara", name: "Guadalajara", x: 483, y: 423, label: { dx: 0, dy: 20, anchor: "middle" } },
  { id: "irapuato", name: "Irapuato", x: 542, y: 423, label: { dx: 0, dy: -11, anchor: "middle" } },
  { id: "queretaro", name: "Querétaro", x: 571, y: 426, label: { dx: 11, dy: 18, anchor: "start" } },
];

const byId = (id) => (id === HUB.id ? HUB : CITIES.find((c) => c.id === id));

const PORTS = [
  { id: "ensenada", name: "Ensenada", x: 92.9, y: 56.1, soon: true },
  { id: "altamira", name: "Altamira", x: 644, y: 366 },
  { id: "manzanillo", name: "Manzanillo", x: 454, y: 475 },
];
const portById = (id) => PORTS.find((p) => p.id === id);

// Unlabeled elbow points for the dotted northwest corridor and the
// Durango-Zacatecas leg — not real destinations. Also hand-placed with
// tools/map-editor.html.
const BEND_1 = { x: 256, y: 94.3 };
const BEND_2 = { x: 287.2, y: 76.5 };
const BEND_3 = { x: 332.7, y: 76.4 };
// Kinks the Durango->Zacatecas line off the Torreón->Zacatecas line it was
// running almost parallel to — a visible detour before it reaches Zacatecas.
const DURANGO_ZACATECAS_BEND = { x: 499.9, y: 331.6 };

// Every route segment, reviewed against the reference map 1:1 — which
// cities connect to which, and solid (active corridor) vs. dotted
// (Ensenada/Hermosillo expansion corridor, matching the "Próximamente:
// puerto de Ensenada" copy elsewhere on this page).
const ROUTES = [
  // northwest corridor — dotted (expansion)
  { points: [byId("tijuana"), byId("mexicali")], dotted: true },
  { points: [byId("tijuana"), portById("ensenada")], dotted: true },
  { points: [byId("mexicali"), BEND_1, byId("hermosillo")], dotted: true },
  { points: [byId("mexicali"), BEND_1, BEND_2, BEND_3, byId("chihuahua")], dotted: true },
  // trunk — solid
  { points: [byId("chihuahua"), HUB] },
  { points: [HUB, byId("torreon")] },
  // active network — solid
  { points: [byId("torreon"), byId("saltillo")] },
  { points: [byId("saltillo"), byId("monterrey")] },
  { points: [byId("torreon"), byId("durango")] },
  { points: [byId("torreon"), byId("zacatecas")] },
  { points: [byId("durango"), DURANGO_ZACATECAS_BEND, byId("zacatecas")] },
  { points: [byId("saltillo"), byId("slp")] },
  { points: [byId("monterrey"), portById("altamira")] },
  { points: [byId("slp"), portById("altamira")] },
  { points: [byId("zacatecas"), byId("aguascalientes")] },
  { points: [byId("zacatecas"), byId("slp")] },
  { points: [byId("slp"), byId("guadalajara")] },
  { points: [byId("aguascalientes"), byId("irapuato")] },
  { points: [byId("slp"), byId("queretaro")] },
  { points: [byId("irapuato"), byId("queretaro")] },
  { points: [byId("guadalajara"), portById("manzanillo")] },
];

const pathFrom = (points) =>
  points.map((p, i) => `${i === 0 ? "M" : "L"} ${p.x} ${p.y}`).join(" ");

export function CoverageMap() {
  const { t } = useTranslation();
  const [active, setActive] = useState(null);

  return (
    <div className="coverage-map">
      {/* ?v= is a cache-buster tied to this rebuild — the JS bundle that
          reads city coordinates gets a fresh hashed filename on every
          deploy automatically, but this plain <img src> did not, so an
          old browser tab (or an intermediate cache ignoring our
          no-store header) could go on showing an old map image under
          brand-new dot coordinates indefinitely otherwise. */}
      <img
        src={`${process.env.PUBLIC_URL}/coverage-map.svg?v=20260915-real-data`}
        alt=""
        className="coverage-map-base"
      />

      <svg className="coverage-map-overlay" viewBox="0 0 1014 662" aria-hidden="true">
        <defs>
          <filter id="mapDotGlow" x="-100%" y="-100%" width="300%" height="300%">
            <feGaussianBlur stdDeviation="4" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {/* Plain fade-in on mount (not a scroll-triggered pathLength "draw
            the line" animation) — that version depended on each path's own
            whileInView visibility firing individually, which proved
            unreliable (routes stayed stuck invisible, or half-drawn, in
            real testing). A route that fails to animate now still just
            appears — it can't get stuck missing. */}
        {ROUTES.map(({ points, dotted }, i) => (
          <path
            key={i}
            d={pathFrom(points)}
            className={`coverage-map-route${dotted ? " coverage-map-route--dotted" : ""} coverage-map-route--fade-in`}
            fill="none"
            style={{ animationDelay: `${0.15 + i * 0.04}s` }}
          />
        ))}

        <g
          className={`coverage-map-marker coverage-map-marker--hub${active === HUB.id ? " is-active" : ""}`}
          onMouseEnter={() => setActive(HUB.id)}
          onMouseLeave={() => setActive(null)}
        >
          <circle cx={HUB.x} cy={HUB.y} r={11} className="coverage-map-hub-ring" />
          <circle cx={HUB.x} cy={HUB.y} r={7} className="coverage-map-hub-dot" filter="url(#mapDotGlow)" />
          <text x={HUB.x + 15} y={HUB.y - 9} className="coverage-map-label coverage-map-label--hub">
            {HUB.name}
          </text>
        </g>

        {CITIES.map((c) => {
          const { dx = 11, dy = 3, anchor = "start" } = c.label || {};
          return (
            <g
              key={c.id}
              className={`coverage-map-marker${active === c.id ? " is-active" : ""}`}
              onMouseEnter={() => setActive(c.id)}
              onMouseLeave={() => setActive(null)}
            >
              <circle cx={c.x} cy={c.y} r={15} className="coverage-map-hit" />
              <circle cx={c.x} cy={c.y} r={6} className="coverage-map-dot" />
              <text
                x={c.x + dx}
                y={c.y + dy}
                textAnchor={anchor}
                className={`coverage-map-label coverage-map-label--${anchor}`}
              >
                {c.name}
              </text>
            </g>
          );
        })}

        {PORTS.map((p) => (
          <g
            key={p.id}
            className={`coverage-map-marker coverage-map-marker--port${p.soon ? " is-soon" : ""}${
              active === p.id ? " is-active" : ""
            }`}
            onMouseEnter={() => setActive(p.id)}
            onMouseLeave={() => setActive(null)}
          >
            {p.soon && <circle cx={p.x} cy={p.y} r={11} className="coverage-map-pulse" />}
            <circle cx={p.x} cy={p.y} r={15} className="coverage-map-hit" />
            <path
              d={`M ${p.x} ${p.y - 8} L ${p.x + 7} ${p.y + 5} L ${p.x - 7} ${p.y + 5} Z`}
              className="coverage-map-port-mark"
            />
            <text x={p.x + 12} y={p.y + 4} className="coverage-map-label coverage-map-label--port">
              {p.name}
              {p.soon ? ` (${t("coverage.soonShort")})` : ""}
            </text>
          </g>
        ))}
      </svg>
    </div>
  );
}

export default CoverageMap;
